import { QRCodeSVG } from 'qrcode.react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog.tsx'

/**
 * PasswordResetSuccessModal
 *
 * Local-only success modal shown on `app.germanmethod.com` (qsk-web) right
 * after a successful password reset. The modal stays *inside* qsk-web — it
 * does NOT navigate cross-domain — and serves two jobs:
 *
 *  1. Confirm to the user that their password has been updated.
 *  2. Push the German Method mobile app, since the web app at
 *     `app.germanmethod.com` is intentionally feature-thin.
 *
 * Visually mirrors `german-method-landingpage`'s `MigrationConfirmationModal`
 * (white card, navy heading, gradient navy → purple primary button, outlined
 * Google Play button, hairline divider, OneLink QR with rounded border).
 *
 * COPY POLICY
 * -----------
 * Strings are hardcoded German on purpose. The audience for the qsk-web
 * password-reset flow is overwhelmingly German speakers (QSK migration
 * cohort + new German Method registrations), and the i18n browser-language
 * detector was sometimes resolving to English for users on macOS/Chrome.
 * Hardcoding matches the landing-page migration modal's precedent.
 *
 * NAVIGATION CONTRACT
 * -------------------
 * The modal is purely presentational. Closing the modal — via the X button,
 * via clicking outside (Radix Dialog `onOpenChange`), via Escape, *or* via
 * the primary "Zur Verwaltung" CTA — fires `onContinue`. The parent
 * (ResetPasswordPage) is responsible for the actual `navigate(...)` call so
 * that whichever way the user dismisses, they always land at the next page.
 *
 * STORE URL POLICY
 * ----------------
 * The two app-store buttons use *native* App Store / Google Play URLs (NOT
 * OneLink) by deliberate design — clicking a button on a known platform
 * should land the user directly on the right store, no extra hop.
 *
 * The QR code is the *one* exception: a single QR can't be platform-aware,
 * so it points at `germanmethod.onelink.me/...`, which routes to the right
 * store automatically.
 *
 * a11y: shadcn's `DialogTitle` / `DialogDescription` wire up
 * `aria-labelledby` / `aria-describedby` automatically. shadcn Dialog also
 * handles body scroll lock + Escape key + the corner X close button.
 */

interface PasswordResetSuccessModalProps {
  open: boolean
  email: string | null
  /** Fired by the X close, by clicking outside, AND by the Continue CTA. */
  onContinue: () => void
}

const APP_STORE_URL =
  'https://apps.apple.com/sa/app/german-method/id6756939496'
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.germanmethod.app&pcampaignid=web_share'
const ONELINK_QR_URL = 'https://germanmethod.onelink.me/pW0N/xqph5jsq'

export function PasswordResetSuccessModal({
  open,
  email,
  onContinue,
}: PasswordResetSuccessModalProps) {
  // Single source of truth for "the modal closed for any reason".
  const handleOpenChange = (next: boolean) => {
    if (!next) onContinue()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[500px] p-8 md:p-10 bg-white rounded-3xl border-0 shadow-xl">
        <DialogTitle className="text-[#150958] font-medium text-2xl md:text-[28px] leading-tight tracking-[-0.5px] mb-3 text-left">
          Fertig!
        </DialogTitle>

        <DialogDescription className="text-[#453f3d] text-sm md:text-base mb-6 text-left">
          {email ? (
            <>
              Dein Passwort wurde erfolgreich aktualisiert. Lade dir die
              German Method App herunter und melde dich mit{' '}
              <strong className="text-[#150958] break-all">{email}</strong>{' '}
              an.
            </>
          ) : (
            'Dein Passwort wurde erfolgreich aktualisiert. Lade dir die German Method App herunter und melde dich an.'
          )}
        </DialogDescription>

        <div className="border-t border-[#9cc1e7]/40 pt-6">
          <p className="text-[#150958] font-medium text-sm md:text-base text-center mb-4">
            Lade die German Method App herunter
          </p>
          <div className="flex flex-col sm:flex-row gap-3 w-full">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#150958] to-[#2d13be] text-white font-semibold text-base px-6 py-[16px] rounded-full hover:opacity-90 transition-opacity"
            >
              <svg
                aria-hidden="true"
                className="w-[13px] h-[16px]"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <span>App Store</span>
            </a>

            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-[5px] bg-white border border-[#9cc1e7] text-[#150958] font-semibold text-base px-6 py-[16px] rounded-full hover:bg-[#eef4ff] transition-colors"
            >
              <svg
                aria-hidden="true"
                className="w-[14px] h-[16px]"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M3.18 23.72c-.36-.17-.6-.52-.6-.92V1.2c0-.4.24-.75.6-.92L14.56 12 3.18 23.72zm1.4.56L16.25 13.4l2.67 2.67L5.8 24.28c-.43.24-.89.23-1.22 0zM16.25 10.6L4.58-.28c.33-.23.79-.24 1.22 0l13.12 8.21-2.67 2.67zm1.27 1.27l3.37-3.37c.6.34.98.95.98 1.5s-.38 1.16-.98 1.5l-3.37-3.37v3.74z" />
              </svg>
              <span>Google Play</span>
            </a>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-center">
          <div className="flex items-center w-full gap-3 mt-2 mb-3">
            <div className="flex-1 h-px bg-[#9cc1e7]/40" />
            <span className="text-[#757372] text-xs uppercase tracking-wide">
              Oder scannen zum Download
            </span>
            <div className="flex-1 h-px bg-[#9cc1e7]/40" />
          </div>
          <div className="bg-white rounded-xl p-3 border border-[#9cc1e7]/40">
            <QRCodeSVG
              value={ONELINK_QR_URL}
              size={144}
              fgColor="#150958"
              bgColor="#ffffff"
              level="M"
            />
          </div>
          <p className="text-[#757372] text-xs mt-3 text-center">
            Funktioniert auf iOS und Android
          </p>
        </div>

        <button
          type="button"
          onClick={onContinue}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#150958] to-[#2d13be] text-white font-semibold text-base px-6 py-[16px] rounded-full hover:opacity-90 transition-opacity"
        >
          <span>Zur Verwaltung</span>
          <span aria-hidden="true">→</span>
        </button>
      </DialogContent>
    </Dialog>
  )
}
